import React, { useRef, useState } from 'react';
import useOnClickOutside from '../../hook/useOnClickOutside';
import * as precoService from '../../services/calculs/applyPreconisation';
import { currencyFormat, parseCurrency, percentFormat } from '../../services/tools/TypeHelper';
import { CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useRecoilValue } from 'recoil';
import { themesStateAtom, ticketStateAtom } from '../../services/Recoil/Atom/Themes.atom';
import ProjectionFinanciereCustomTooltip from './CustomTooltip';
import { totalProjectionFinanciere } from '../../services/calculs/applyPreconisation';

// Style
import './ModaleProjectionFinanciere.scss';

// Icons
import { ReactComponent as IconClose } from '../../assets/icons/icon-close.svg';
import { ReactComponent as IconGraph } from '../../assets/icons/simulator/icon-graph.svg';
import { ReactComponent as IconTable } from '../../assets/icons/simulator/icon-table.svg';
import { getFirstPackBattery } from '../../services/calculs/themesTools';

type ModalFooterProps = Array<{
    title: string;
    colspan?: number | null;
}>;

interface Props {
    topTitle?: string;
    closeTop?: boolean;
    title: string;
    close: () => void;
    projectionFinanciere: precoService.ProjectionFinanciere;
}

type TabsProps = {
    selectedTab: number;
    setSelectedTab: (tab: number) => void;
};

type CrossingProps = {
    annee: number;
    gainCumule: number;
};

const Tabs = ({ selectedTab, setSelectedTab }: TabsProps) => {
    const handleClick = (tab: number) => {
        setSelectedTab(tab);
    };

    return (
        <div className="tab-container">
            <div className="btn-group">
                <button type="button" className="btn btn-tab-icon" onClick={() => handleClick(0)} data-active={selectedTab === 0}>
                    <IconTable height={24} width={24} fill="#4a5e6a" />
                </button>
                <button type="button" className="btn btn-tab-icon" onClick={() => handleClick(1)} data-active={selectedTab === 1}>
                    <IconGraph height={24} width={24} fill="#4a5e6a" />
                </button>
            </div>
        </div>
    );
};

const ModaleProjectionFinanciere: React.FC<Props> = ({ topTitle, title, close, closeTop, projectionFinanciere }) => {
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const ref = useRef(null);
    const ticket = useRecoilValue(ticketStateAtom);
    const themes = useRecoilValue(themesStateAtom);

    const annuel = projectionFinanciere?.projectionsAnnuelles ?? new Array<precoService.ProjectionFinanciereAnnuelle>();

    const hasBatt = getFirstPackBattery(themes) !== undefined;

    // est-ce qu'on affiche la colonne auto conso ?
    const showAutoconso = annuel.some((e) => {
        return e.autoconso_pv !== 0;
    });
    // est-ce qu'on affiche la colonne autoConsoRatio
    const showAutoconsoRatio = showAutoconso;
    // est-ce qu'on affiche la colonne revente
    console.log('proj fin hasBatt', hasBatt);
    const showAutoconsoBatt = hasBatt || annuel.some((e) => e.autoconso_batt !== 0);
    // const showReventePv = annuel.some((e) => {
    //     return e.revente_pv !== 0;
    // });
    // const showReventeBatt = annuel.some((e) => {
    //     return e.revente_batt !== 0;
    // });
    const showRevente = annuel.some((e) => {
        return e.revente_batt + e.revente_pv !== 0;
    });
    const showEconomieHorsPv = annuel.some((e) => {
        return e.economieHorsPV !== 0;
    });
    //construit les titres de colonnes.
    const titles = new Array<string>();
    titles.push('Année');
    if (showAutoconsoRatio) titles.push('% Autoconso');
    if (showAutoconso) titles.push('Autoconso');
    if (showAutoconsoBatt) titles.push('Autoconso Batt');
    // if (showReventePv) titles.push('Revente Pv');
    // if (showReventeBatt) titles.push('Revente Batt');
    if (showRevente) titles.push('Revente');
    if (showEconomieHorsPv) titles.push('Economie (hors PV)');
    titles.push('Total');
    titles.push('Rendement');

    // construit les footers
    const contentFooter: ModalFooterProps = [
        {
            title: 'Sur 25 ans',
            colspan: titles.length - (showEconomieHorsPv ? 3 : 2),
        },
        ...(showEconomieHorsPv
            ? [
                  {
                      title: currencyFormat(projectionFinanciere.economieHorsPV.prix()),
                  },
              ]
            : []),
        {
            title: currencyFormat(totalProjectionFinanciere(projectionFinanciere)),
        },
        {
            title: percentFormat(projectionFinanciere.rendement),
        },
    ];

    const handleClickOutsite = () => {
        close();
    };

    useOnClickOutside(ref, handleClickOutsite);

    const prepareChartData = (annuel: precoService.ProjectionFinanciereAnnuelle[]) => {
        let cumulativeTotal = 0;
        // Do not touch this line, or it will chier des bulles
        const firstElement = { annee: -1, total: 0 };
        return [firstElement, ...annuel].map((element) => {
            cumulativeTotal += element.total;
            return {
                annee: element.annee + 1,
                gainCumule: cumulativeTotal,
            };
        });
    };

    const findCrossings = (data: CrossingProps[], installationCost: number) => {
        const crossings = [];

        for (let i = 1; i < data.length; i++) {
            const prev = data[i - 1];
            const current = data[i];

            const prevDiff = prev.gainCumule - installationCost;
            const currentDiff = current.gainCumule - installationCost;

            if (prevDiff * currentDiff < 0) {
                const ratio = prevDiff / (prevDiff - currentDiff);
                const crossingAnnee = prev.annee + ratio * (current.annee - prev.annee);

                crossings.push({
                    annee: crossingAnnee,
                });
            }
        }

        return crossings;
    };

    const crossings = findCrossings(prepareChartData(annuel), parseCurrency(ticket.reste_a_charge));
    const total = parseCurrency(ticket.reste_a_charge);

    const startYear = Math.floor(crossings[0].annee);
    const endYear = startYear + 1;

    const handleSuffixYear = (year: number) => {
        if (year === 1) {
            return year + ' ère';
        }

        return year + ' ème';
    };

    const chartData = prepareChartData(annuel);

    return (
        <div className="modal modal-custom modal-finance" tabIndex={0} role="dialog" aria-labelledby={title}>
            <div className="modal-dialog" role="document">
                <div className="modal-content" ref={ref}>
                    {topTitle && <p className="modal-top-title">{topTitle}</p>}
                    {closeTop && (
                        <button type="button" className="btn btn-modal-close" onClick={handleClickOutsite}>
                            <IconClose fill="#FD5C21" />
                        </button>
                    )}
                    <div className="modal-header mb-5">
                        <h2 className="modal-title">
                            {title}
                            <Tabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
                        </h2>
                    </div>

                    <div className="modal-body">
                        {selectedTab === 0 && (
                            <table className="table table-modal">
                                <thead>
                                    <tr>
                                        {titles.map((title: string, index: number) => (
                                            <th key={index}>{title}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {annuel?.map((element, index: number) => (
                                        <tr key={index}>
                                            <td>{element.annee + 1}</td>
                                            {showAutoconsoRatio && <td>{percentFormat(element.tauxAutoconso)}</td>}
                                            {showAutoconso && <td>{currencyFormat(element.autoconso_pv)}</td>}
                                            {showAutoconsoBatt && <td>{currencyFormat(element.autoconso_batt)}</td>}
                                            {/* {showReventePv && <td>{currencyFormat(element.revente_pv)}</td>}
                                            {showReventeBatt && <td>{currencyFormat(element.revente_batt)}</td>} */}
                                            {showRevente && <td>{currencyFormat(element.revente_pv + element.revente_batt)}</td>}
                                            {showEconomieHorsPv && <td>{currencyFormat(element.economieHorsPV)}</td>}
                                            <td>{currencyFormat(element.total)}</td>
                                            <td>{percentFormat(element.rendement, 2)}</td>
                                        </tr>
                                    ))}
                                    <tr className="total">
                                        {contentFooter &&
                                            contentFooter.map((footer, index: number) => (
                                                <td colSpan={footer.colspan ? +footer.colspan : 0} key={index}>
                                                    {footer.title}
                                                </td>
                                            ))}
                                    </tr>
                                </tbody>
                            </table>
                        )}

                        {selectedTab === 1 && (
                            <div className="row" style={{ height: '500px' }}>
                                <div className="col-12 col-md-8">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ left: 45, right: 10 }}>
                                            <CartesianGrid strokeDasharray="3 3" />

                                            {/* Axe X pour l'année */}
                                            <XAxis dataKey="annee" type="number" domain={[0, 25]} ticks={Array.from({ length: 26 }, (_, i) => i)} />

                                            {/* Axe Y à gauche pour le Gain cumulé (en euros) */}
                                            <YAxis
                                                yAxisId="left"
                                                label={{ value: 'Trésorerie cumulée', angle: -90, position: 'center', dx: -60 }}
                                                tickFormatter={(value: number) => `${currencyFormat(value)}`}
                                            />

                                            {/* Afficher les informations au survol */}
                                            <Tooltip
                                                content={<ProjectionFinanciereCustomTooltip />}
                                                cursor={{ fill: 'rgba(225, 225, 225, 0.5)' }}
                                                wrapperStyle={{ outline: 'none' }}
                                            />

                                            {/* Ligne bleue pour le Gain cumulé */}
                                            <Line yAxisId="left" type="monotone" dataKey="gainCumule" name="Trésorerie cumulée" stroke="#349f4a" dot={false} />

                                            {/* Ligne turquoise horizontale pour le Coût total de l'installation */}
                                            <ReferenceLine y={Math.round(total)} yAxisId="left" stroke="#f58920" />

                                            {/* Zone de retour sur investissement */}
                                            <ReferenceArea x1={startYear} x2={endYear} fill="#76ca4f" opacity={0.5} yAxisId="left" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                <div className="col-12 col-md-4">
                                    <div className="modal-content-list" style={{ maxHeight: '470px' }}>
                                        <h2 className="modal-content-list-title">Légende</h2>

                                        <ul className="list-legend">
                                            <li>
                                                <p data-key={'consoEnedis'} className="mb-0 d-flex justify-content-between">
                                                    <span>Coût de l'installation :</span>
                                                    <span className="text-end fw-bold">{ticket.montant_vente}</span>
                                                </p>
                                            </li>
                                            <li>
                                                <p data-key={'consoEnedis'} className="mb-0 d-flex justify-content-between">
                                                    <span>Aide :</span>
                                                    <span className="text-end fw-bold">{ticket.montant_aides_cumul.replace('-', '')}</span>
                                                </p>
                                            </li>
                                            <li>
                                                <p data-key={'consoEnedis'} className="mb-0 d-flex justify-content-between">
                                                    <span>Coût de l'installation aides déduites :</span>
                                                    <span className="text-end fw-bold">{currencyFormat(total)}</span>
                                                </p>
                                            </li>
                                        </ul>

                                        <p data-key={'green'} className="text-center investment-block mb-0 mt-2">
                                            Retour sur investissement dans
                                            <br />
                                            la {handleSuffixYear(endYear)} année
                                        </p>

                                        <h3 className="modal-content-list-subtitle text-center">
                                            Trésorerie cumulée
                                            <span>(Economies + production)</span>
                                        </h3>
                                        <div className="row">
                                            <div className="col-6">
                                                {chartData.slice(1, 13).map((data, index) => (
                                                    <p key={index} className="mb-0 mt-0 d-flex justify-content-between" data-key={'consoEnedis'}>
                                                        <span>Année {data.annee} :</span>
                                                        <span className="text-right fw-bold">{currencyFormat(data.gainCumule)}</span>
                                                    </p>
                                                ))}
                                            </div>
                                            <div className="col-6">
                                                {chartData.slice(13, 25).map((data, index) => (
                                                    <p key={index + 12} className="mb-0 mt-0 d-flex justify-content-between" data-key={'consoEnedis'}>
                                                        <span>Année {data.annee} :</span>
                                                        <span className="text-right fw-bold">{currencyFormat(data.gainCumule)}</span>
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12 text-center">
                                                {chartData[25] && (
                                                    <p className="mb-0 mt-1 d-flex justify-content-center" data-key={'consoEnedis'}>
                                                        <span>Année {chartData[25].annee} :</span>
                                                        <span className="ms-2 fw-bold">{currencyFormat(chartData[25].gainCumule)}</span>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModaleProjectionFinanciere;
